/**
 * Invoice Service
 * Handles all invoice operations
 */

import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  onSnapshot,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, functions } from './firebase';
import {
  calculateGraduatedDiscount,
  calculateRetainerAmount,
  getBillingPeriod,
  generateLineItemDescription,
  calculateDiscountSavings,
} from '../utils/graduatedDiscount';

/**
 * Generate invoice from time entries (via Cloud Function)
 */
export const generateInvoice = async (invoiceData) => {
  try {
    const generateInvoiceFn = httpsCallable(functions, 'generateInvoice');
    const result = await generateInvoiceFn(invoiceData);
    return result.data;
  } catch (error) {
    console.error('Error generating invoice:', error);
    throw error;
  }
};

/**
 * Create manual invoice with custom line items (no time entries)
 * Supports flat fees, project-based billing, and custom items
 */
export const createManualInvoice = async (userId, invoiceData) => {
  try {
    const {
      clientId,
      clientName,
      clientEmail,
      lineItems, // Array of { description, quantity, rate, amount }
      dueDate,
      notes = '',
      currency = 'USD',
      discountPercent = 0,
      discountAmount = 0,
    } = invoiceData;

    // Validate required fields
    if (!clientId || !clientName || !clientEmail) {
      throw new Error('Client information is required');
    }

    if (!lineItems || lineItems.length === 0) {
      throw new Error('At least one line item is required');
    }

    // Calculate totals
    const subtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const tax = invoiceData.tax || 0;
    const finalDiscount = discountAmount || (discountPercent > 0 ? subtotal * (discountPercent / 100) : 0);
    const totalAmount = subtotal - finalDiscount + tax;

    // Generate invoice number (simple implementation - can be improved)
    const invoiceNumber = `INV-${Date.now()}`;

    const invoice = {
      userId,
      clientId,
      clientName,
      clientEmail,
      invoiceNumber,
      type: 'manual', // Distinguish from time-based invoices
      issueDate: serverTimestamp(),
      dueDate: new Date(dueDate),
      status: 'draft',
      lineItems,
      subtotal,
      discountPercent: discountPercent || 0,
      discountAmount: finalDiscount,
      tax,
      totalAmount,
      currency,
      notes,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'invoices'), invoice);
    return { invoiceId: docRef.id, ...invoice };
  } catch (error) {
    console.error('Error creating manual invoice:', error);
    throw error;
  }
};

/**
 * Create invoice from subscription (called by cloud function or manually)
 */
export const createSubscriptionInvoice = async (userId, subscriptionData) => {
  try {
    const {
      clientId,
      clientName,
      clientEmail,
      description,
      amount,
      dueDate,
      currency = 'USD',
      subscriptionId,
    } = subscriptionData;

    const lineItems = [{
      description: description || 'Subscription Service',
      quantity: 1,
      rate: amount,
      amount: amount,
    }];

    const invoice = {
      userId,
      clientId,
      clientName,
      clientEmail,
      invoiceNumber: `INV-${Date.now()}`,
      type: 'subscription',
      subscriptionId,
      issueDate: serverTimestamp(),
      dueDate: new Date(dueDate),
      status: 'pending',
      lineItems,
      subtotal: amount,
      tax: 0,
      totalAmount: amount,
      currency,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'invoices'), invoice);
    return { invoiceId: docRef.id, ...invoice };
  } catch (error) {
    console.error('Error creating subscription invoice:', error);
    throw error;
  }
};

/**
 * Record payment for an invoice
 */
export const recordPayment = async (invoiceId, paymentData) => {
  try {
    const {
      amount,
      paymentDate,
      paymentMethod,
      transactionId = '',
      notes = '',
    } = paymentData;

    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const invoice = invoiceDoc.data();
    const paidAmount = (invoice.paidAmount || 0) + amount;
    const isFullyPaid = paidAmount >= invoice.totalAmount;

    // Update invoice
    await updateDoc(invoiceRef, {
      paidAmount,
      status: isFullyPaid ? 'paid' : 'partial',
      paidAt: isFullyPaid ? serverTimestamp() : invoice.paidAt,
      lastPaymentDate: new Date(paymentDate),
      updatedAt: serverTimestamp(),
    });

    // Create payment record
    const payment = {
      userId: invoice.userId,
      invoiceId,
      invoiceNumber: invoice.invoiceNumber,
      clientId: invoice.clientId || null,
      clientName: invoice.clientName || 'Unknown',
      amount,
      paymentDate: new Date(paymentDate),
      paymentMethod,
      transactionId,
      notes,
      status: 'succeeded',
      createdAt: serverTimestamp(),
    };

    await addDoc(collection(db, 'payments'), payment);

    return { success: true, isFullyPaid };
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error;
  }
};

/**
 * Get invoices for user
 */
export const getInvoices = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'invoices'),
      where('userId', '==', userId),
      orderBy('issueDate', 'desc')
    );

    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters.clientId) {
      q = query(q, where('clientId', '==', filters.clientId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting invoices:', error);
    throw error;
  }
};

/**
 * Get a single invoice
 */
export const getInvoice = async (invoiceId) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    return { id: invoiceDoc.id, ...invoiceDoc.data() };
  } catch (error) {
    console.error('Error getting invoice:', error);
    throw error;
  }
};

/**
 * Update invoice
 * Draft invoices: all fields can be updated
 * Sent/Pending invoices: only admin-allowed fields (currency, notes) can be updated
 */
export const updateInvoice = async (invoiceId, updateData, isAdmin = false) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const invoice = invoiceDoc.data();

    // Admin-allowed fields that can be updated on any non-void invoice
    const adminAllowedFields = ['currency', 'notes', 'dueDate', 'projectName', 'clientName'];

    if (invoice.status === 'void') {
      throw new Error('Void invoices cannot be edited');
    }

    if (invoice.status !== 'draft') {
      if (!isAdmin) {
        throw new Error('Only draft invoices can be edited');
      }
      // For non-draft invoices, admins can only update certain fields
      const restrictedUpdate = {};
      for (const field of adminAllowedFields) {
        if (updateData[field] !== undefined) {
          restrictedUpdate[field] = updateData[field];
        }
      }
      if (Object.keys(restrictedUpdate).length === 0) {
        throw new Error('No editable fields in update request');
      }
      updateData = restrictedUpdate;
    }

    await updateDoc(invoiceRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

/**
 * Finalize invoice (change from draft to pending)
 */
export const finalizeInvoice = async (invoiceId) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    await updateDoc(invoiceRef, {
      status: 'pending',
      sentAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error finalizing invoice:', error);
    throw error;
  }
};

/**
 * Send payment reminder
 */
export const sendPaymentReminder = async (invoiceId) => {
  try {
    const sendReminderFn = httpsCallable(functions, 'sendPaymentReminder');
    const result = await sendReminderFn({ invoiceId });
    return result.data;
  } catch (error) {
    console.error('Error sending reminder:', error);
    throw error;
  }
};

/**
 * Subscribe to invoices
 * @param {string} userId - User ID
 * @param {function} callback - Callback function
 * @param {boolean} isAdmin - If true, returns all invoices regardless of userId
 * @param {object} filters - Optional filters
 */
export const subscribeToInvoices = (userId, callback, isAdmin = false, filters = {}) => {
  // Admins can see all invoices
  let q = isAdmin
    ? query(collection(db, 'invoices'), orderBy('issueDate', 'desc'))
    : query(
        collection(db, 'invoices'),
        where('userId', '==', userId),
        orderBy('issueDate', 'desc')
      );

  if (filters.status) {
    q = query(q, where('status', '==', filters.status));
  }

  return onSnapshot(q, (snapshot) => {
    const invoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(invoices);
  });
};

/**
 * Create retainer invoice with graduated discount support
 * @param {string} userId - User ID
 * @param {Object} retainerData - Retainer invoice data
 * @param {string} retainerData.clientId - Client ID
 * @param {string} retainerData.clientName - Client name
 * @param {string} retainerData.clientEmail - Client email
 * @param {number} retainerData.baseRetainer - Base monthly retainer amount
 * @param {Object} retainerData.graduatedDiscount - Graduated discount settings
 * @param {string} retainerData.billingPeriod - Billing period (YYYY-MM format)
 * @param {string} retainerData.dueDate - Due date
 * @param {string} retainerData.currency - Currency code
 * @param {string} retainerData.notes - Optional notes
 */
export const createRetainerInvoice = async (userId, retainerData) => {
  try {
    const {
      clientId,
      clientName,
      clientEmail,
      baseRetainer,
      graduatedDiscount,
      billingPeriod = getBillingPeriod(),
      dueDate,
      currency = 'USD',
      notes = '',
    } = retainerData;

    // Validate required fields
    if (!clientId || !clientName || !clientEmail) {
      throw new Error('Client information is required');
    }

    if (!baseRetainer || baseRetainer <= 0) {
      throw new Error('Base retainer amount is required');
    }

    // Calculate graduated discount
    const discountInfo = calculateGraduatedDiscount(graduatedDiscount);
    const discountSavings = calculateDiscountSavings(baseRetainer, discountInfo.discountPercent);
    const finalAmount = discountSavings.finalAmount;

    // Generate line item description with discount info
    const description = generateLineItemDescription(billingPeriod, discountInfo);

    // Create line item
    const lineItems = [
      {
        description,
        quantity: 1,
        rate: finalAmount,
        amount: finalAmount,
        // Store discount details for reference
        baseAmount: baseRetainer,
        discountPercent: discountInfo.discountPercent || 0,
        discountAmount: discountSavings.discountAmount || 0,
      },
    ];

    // Generate invoice number
    const invoiceNumber = `INV-${Date.now()}`;

    const invoice = {
      userId,
      clientId,
      clientName,
      clientEmail,
      invoiceNumber,
      type: 'retainer',
      billingPeriod,
      issueDate: serverTimestamp(),
      dueDate: new Date(dueDate),
      status: 'draft',
      lineItems,
      subtotal: finalAmount,
      tax: 0,
      totalAmount: finalAmount,
      currency,
      notes,
      // Store graduated discount info
      graduatedDiscount: discountInfo.enabled
        ? {
            enabled: true,
            startDate: graduatedDiscount.startDate,
            schedule: discountInfo.schedule,
            currentYear: discountInfo.currentYear,
            discountPercent: discountInfo.discountPercent,
          }
        : null,
      baseRetainer,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'invoices'), invoice);
    return { invoiceId: docRef.id, ...invoice, discountInfo };
  } catch (error) {
    console.error('Error creating retainer invoice:', error);
    throw error;
  }
};

/**
 * Send invoice to client via Cloud Function
 * @param {string} invoiceId - Invoice ID to send
 * @returns {Promise<Object>} Result with success status
 */
export const sendInvoice = async (invoiceId) => {
  try {
    const sendInvoiceFn = httpsCallable(functions, 'sendInvoiceToClient');
    const result = await sendInvoiceFn({ invoiceId });
    return result.data;
  } catch (error) {
    console.error('Error sending invoice:', error);
    throw error;
  }
};

/**
 * Delete a draft invoice
 * Only draft invoices can be deleted
 * @param {string} invoiceId - Invoice ID to delete
 * @returns {Promise<Object>} Result with success status
 */
export const deleteInvoice = async (invoiceId) => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const invoice = invoiceDoc.data();
    if (invoice.status !== 'draft') {
      throw new Error('Only draft invoices can be deleted');
    }

    await deleteDoc(invoiceRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

/**
 * Void an invoice
 * Voiding is used for issued invoices that should no longer be paid
 * @param {string} invoiceId - Invoice ID to void
 * @param {string} reason - Reason for voiding
 * @returns {Promise<Object>} Result with success status
 */
export const voidInvoice = async (invoiceId, reason = '') => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceDoc = await getDoc(invoiceRef);

    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const invoice = invoiceDoc.data();

    // Can't void already paid or voided invoices
    if (invoice.status === 'paid') {
      throw new Error('Cannot void a paid invoice');
    }
    if (invoice.status === 'void') {
      throw new Error('Invoice is already voided');
    }

    await updateDoc(invoiceRef, {
      status: 'void',
      voidedAt: serverTimestamp(),
      voidReason: reason,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error voiding invoice:', error);
    throw error;
  }
};
